import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';

import { ButtonComponent, FilesToUpload } from '@akostm/orbit-ui';
import {
  faCamera,
  faImage,
  faLocationDot,
  faMicrophone,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FooterButtonType, MessageSenderType } from '../../../interfaces';
import { MessageKind, MessageTemplateLight, ConversationTypeTemplateKind } from '@clientwire/api';
import { faLocationQuestion } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { NativePermissionName, NativePermissionsService } from '../../../services';

@Component({
  selector: 'shared-footer-addon',
  standalone: true,
  imports: [ButtonComponent, TranslatePipe],
  templateUrl: './footer-addon.component.html',
  styleUrl: './footer-addon.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterAddonComponent {
  senderType = input.required<MessageSenderType>();
  templates = input<Map<string, MessageTemplateLight> | undefined>();
  deletedAttachment = input<MessageKind | null>(null);
  files = model<FilesToUpload[]>();
  buttonClick = output<FooterButtonType>();
  photoUploader = viewChild<ElementRef>('photoUploader');
  cameraUploader = viewChild<ElementRef>('cameraUploader');
  audioUploader = viewChild<ElementRef>('audioUploader');
  videoUploader = viewChild<ElementRef>('videoUploader');

  readonly imageIcon = faImage;
  readonly cameraIcon = faCamera;
  readonly microphoneIcon = faMicrophone;
  readonly locationDotIcon = faLocationDot;
  readonly locationRequestIcon = faLocationQuestion;
  readonly videoIcon = faVideo;
  readonly footerButtonTypeEnum = FooterButtonType;
  readonly messageSenderTypeEnum = MessageSenderType;
  readonly #nativePermissions = inject(NativePermissionsService);
  #reentryGuardKey: string | null = null;

  readonly showRequestLocationButton = computed(() => {
    const templates = this.templates();
    return templates?.has(ConversationTypeTemplateKind.RequestLocation) ?? false;
  });

  readonly isSafari = signal(false);
  readonly supportsCaptureAttribute = signal(false);

  constructor() {
    // Detect Safari on iOS without using deprecated navigator.vendor
    // Check for Safari in user agent, exclude Chrome/Chromium, and verify touch support
    const ua = navigator.userAgent;
    const isSafariBrowser = /safari/i.test(ua) && !/chrome|chromium|crios/i.test(ua);
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    this.isSafari.set(isSafariBrowser && isIOS && navigator.maxTouchPoints > 0);

    // Feature detection for capture attribute
    this.supportsCaptureAttribute.set(this.#detectCaptureSupport());

    effect(() => {
      const photoUploader = this.photoUploader();
      const cameraUploader = this.cameraUploader();
      const audioUploader = this.audioUploader();
      const videoUploader = this.videoUploader();
      const dA = this.deletedAttachment();
      if (this.deletedAttachment()) {
        switch (dA) {
          case 'IMAGE':
            if (photoUploader?.nativeElement?.value) {
              photoUploader.nativeElement.value = '';
            }
            if (cameraUploader?.nativeElement?.value) {
              cameraUploader.nativeElement.value = '';
            }
            break;
          case 'VIDEO':
            if (videoUploader?.nativeElement?.value) {
              videoUploader.nativeElement.value = '';
            }
            break;
          case 'AUDIO':
            if (audioUploader?.nativeElement?.value) {
              audioUploader.nativeElement.value = '';
            }
            break;
        }
      }
    });
  }

  onFileSelected(event: Event, _type: FooterButtonType) {
    const target = event.target as HTMLInputElement;
    if (event && target) {
      const array: FilesToUpload[] = target.files
        ? Array.from(target.files).map((file) => {
            return {
              file: file as File,
              src: '',
            };
          })
        : [];
      this.files.set(array);
    }
  }

  onAlbumButtonClick(): void {
    if (this.#shouldSkipAction('album')) {
      return;
    }
    void this.#triggerMediaAction([], () => this.#openHiddenInput(this.photoUploader(), 'album'));
  }

  onCameraButtonClick(): void {
    if (this.#shouldSkipAction('camera')) {
      return;
    }
    const action = () => {
      if (this.supportsCaptureAttribute()) {
        this.#openHiddenInput(this.cameraUploader(), 'camera');
      } else {
        this.buttonClick.emit(this.footerButtonTypeEnum.WebCamera);
      }
    };

    void this.#triggerMediaAction(['camera'], action);
  }

  onVideoButtonClick(): void {
    if (this.#shouldSkipAction('video')) {
      return;
    }
    void this.#triggerMediaAction(['camera', 'microphone'], () =>
      this.#openHiddenInput(this.videoUploader(), 'video')
    );
  }

  onAudioButtonClick(): void {
    if (this.#shouldSkipAction('audio')) {
      return;
    }
    void this.#triggerMediaAction(['microphone'], () =>
      this.#openHiddenInput(this.audioUploader(), 'audio')
    );
  }

  #openHiddenInput(ref?: ElementRef<HTMLInputElement | null>, guardKey?: string): void {
    const input = ref?.nativeElement;
    if (!input) {
      return;
    }

    if (guardKey) {
      this.#reentryGuardKey = guardKey;
    }
    input.click();
  }

  #detectCaptureSupport(): boolean {
    // Create a temporary input element to test capture support
    const input = document.createElement('input');
    input.type = 'file';

    // Check if the capture property exists and if it's supported
    return 'capture' in input && this.#isMobileDevice();
  }

  #isMobileDevice(): boolean {
    // Check if it's a mobile device where capture attribute is most useful
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      navigator.maxTouchPoints > 0
    );
  }

  onPhotoKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onAlbumButtonClick();
    }
  }

  onCameraKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCameraButtonClick();
    }
  }

  onWebCameraKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCameraButtonClick();
    }
  }

  onVideoKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onVideoButtonClick();
    }
  }

  onAudioKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onAudioButtonClick();
    }
  }

  onLocationKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.buttonClick.emit(this.footerButtonTypeEnum.Location);
    }
  }

  onLocationRequestKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.buttonClick.emit(this.footerButtonTypeEnum.MsgLocation);
    }
  }

  async #triggerMediaAction(
    permissions: NativePermissionName[],
    onGranted: () => void
  ): Promise<void> {
    if (!permissions.length) {
      onGranted();
      return;
    }

    const allGranted = await this.#requestPermissions(permissions);
    if (allGranted) {
      onGranted();
    }
  }

  async #requestPermissions(permissions: NativePermissionName[]): Promise<boolean> {
    for (const permission of permissions) {
      const granted = await this.#nativePermissions.requestPermission(permission);
      if (!granted) {
        return false;
      }
    }
    return true;
  }

  #shouldSkipAction(key: string): boolean {
    if (this.#reentryGuardKey === key) {
      this.#reentryGuardKey = null;
      return true;
    }
    return false;
  }
}
